import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";

import {
  emptyString,
  GET_RESPONSE_TYPE,
  sessionStorageKey,
  individualGroupPerformanceExportHeaders,
  performanceScreensConstants,
  AVERAGE_RATING_RESPONSE_TYPE,
  PERFORMANCE_FILTER_DEFAULT_MIN_STAR,
  PERFORMANCE_FILTER_INITIAL_MAX_STAR,
  PERFORMANCE_EXPORT_CSV_DELAY_MS,
  PERFORMANCE_DEFAULT_SORTING,
} from "../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  fileName,
  isInclude,
  calculateChange,
  escapeSearchQuery,
  convertToDateToUnix,
  updatedColumnHeaders,
  convertFromDateToUnix,
  datePreferenceUtility,
  useRatingCheckboxGroup,
  guestSentimentPaginatedPostRequestBody,
} from "../../../../shared";
import {
  brandPerformanceActions,
  individualGroupPerformanceActions,
} from "../../redux/actions";
import FilterComponent from "./filterComponent";
import GroupPerformance from "./groupPerformance";
import {
  buildSurveyCompletionDateFilterQuery,
  buildDatedPerformanceRequestPayload,
  mapStoresPerformanceExportRecords,
} from "../shared/performanceQueryUtility";

const { GUEST_SENTIMENTS, GUEST_TRANSACTION } = performanceScreensConstants;

const componentName = "Group Performance (Stores)";

const IndividualGroupPerformance = (props) => {
  const {
    frRoute,
    systemReducer,
    individualGroupPerformanceReducer: {
      allItemsForExport,
      franchiseResponsesData,
      allFranchiseResponsesData,
      groupResponseDataPagination,
      groupsAvgSentimentAndTransactionRatingData,
    },
    resetAllExportItems,
    getRecordPaginationResponses,
    getAllFranchiseResponsesItemsForExport,
    getAverageRatingResponses,
  } = props;

  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [minStar, setMinStar] = useState(PERFORMANCE_FILTER_DEFAULT_MIN_STAR);
  const [maxStar, setMaxStar] = useState(PERFORMANCE_FILTER_INITIAL_MAX_STAR);
  const [starValue, setStarValue] = useState([]);
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [queryString, setQueryString] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);
  const [selectedOption, setSelectedOption] = useState([]);
  const [sorting, setSorting] = useState(PERFORMANCE_DEFAULT_SORTING);
  const [dateState, setDateState] = useState(datePreferenceUtility());

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const { handleRatingOptionChange, selectedRatingOption } =
    useRatingCheckboxGroup(selectedOption, setStarValue, setSelectedOption);

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy, size) => {
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    const requestBody = guestSentimentPaginatedPostRequestBody({
      pagination: groupResponseDataPagination,
      pageNumber: pageNumber,
      sortBy: sortBy || sorting,
      filterQuery: queryString,
      averageSentiments: averageSentiments,
      isFrRoute: frRoute,
      size: size,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    });

    if (sortBy) {
      setSorting(sortBy);
    }
    return { ...requestBody, search: escapeSearchQuery(searchString) };
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    const groupId = getItemFromStorage(sessionStorageKey.FRANCHISEE__ID);
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);

    if (
      (isInclude(requestBody.filter, GUEST_SENTIMENTS) &&
        selectedRatingOption === GUEST_SENTIMENTS) ||
      (isInclude(requestBody.filter, GUEST_TRANSACTION) &&
        selectedRatingOption === GUEST_TRANSACTION) ||
      !(
        isInclude(requestBody.filter, GUEST_TRANSACTION) ||
        isInclude(requestBody.filter, GUEST_SENTIMENTS)
      )
    ) {
      const { GET_GROUP_USER_RESPONSES } = GET_RESPONSE_TYPE;
      getRecordPaginationResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: groupId,
          type: GET_GROUP_USER_RESPONSES,
          requestBody,
        }),
      );
      const { GET_GROUPS_AVERAGE_RATING_RESPONSES } =
        AVERAGE_RATING_RESPONSE_TYPE;

      getAverageRatingResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: groupId,
          type: GET_GROUPS_AVERAGE_RATING_RESPONSES,
          requestBody,
        }),
      );
    }
  };

  //Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current?.link.click();
    }, PERFORMANCE_EXPORT_CSV_DELAY_MS);
  };

  //Get all data for export
  const getExportData = () => {
    const groupId = getItemFromStorage(sessionStorageKey.FRANCHISEE__ID);
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    const requestBody = guestSentimentPaginatedPostRequestBody({
      pagination: {
        size: groupResponseDataPagination.size,
      },
      size: groupResponseDataPagination.size,
      pageNumber: groupResponseDataPagination.number,
      sortBy: sorting,
      filterQuery: queryString,
      averageSentiments,
      isFrRoute: frRoute,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    });
    requestBody.search = searchString;

    getAllFranchiseResponsesItemsForExport(
      createDateFrom,
      createDateTo,
      groupId,
      requestBody,
    );
    setExportHeaders(
      updatedColumnHeaders(individualGroupPerformanceExportHeaders, dayCounts),
    );
  };

  useEffect(() => {
    if (queryString || searchString) {
      getData(0, null, groupResponseDataPagination.size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, queryString, selectedRatingOption]);

  useEffect(() => {
    setQueryString(
      buildSurveyCompletionDateFilterQuery({
        createDateFrom,
        createDateTo,
        starValue,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, createDateTo, createDateFrom, starValue]);

  useEffect(() => {
    if (allItemsForExport.length) {
      setExportData(
        mapStoresPerformanceExportRecords(allItemsForExport, calculateChange),
      );
      exportCSV();
      resetAllExportItems();
    }
    // eslint-disable-next-line
  }, [allItemsForExport]);

  return (
    <>
      <FilterComponent
        dateState={dateState}
        setMinStar={setMinStar}
        setMaxStar={setMaxStar}
        setStarValue={setStarValue}
        setDateState={setDateState}
        setDayCounts={setDayCounts}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        selectedRatingOption={selectedRatingOption}
        handleRatingOptionChange={handleRatingOptionChange}
      />
      <GroupPerformance
        sorting={sorting}
        getData={getData}
        dayCounts={dayCounts}
        exportFunc={getExportData}
        selectedOption={selectedOption}
        systemReducer={systemReducer}
        searchString={searchString}
        setSearchString={setSearchString}
        userResponsesData={franchiseResponsesData}
        totalRestaurants={allFranchiseResponsesData.totalItems}
        totalResponses={allFranchiseResponsesData.totalResponses}
        userResponseDataPagination={groupResponseDataPagination}
        avgSentimentAndTransactionRatingData={
          groupsAvgSentimentAndTransactionRatingData
        }
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={exportHeaders}
        data={exportData}
        filename={`${fileName(
          componentName,
          createDateFrom,
          createDateTo,
        )}.csv`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  systemReducer: state.system,
  individualGroupPerformanceReducer: state.individualGroupPerformanceReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllFranchiseResponsesItemsForExport: (
    createDateFrom,
    createDateTo,
    brandId,
    body,
  ) =>
    dispatch(
      individualGroupPerformanceActions.getAllFranchiseResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        brandId,
        body,
      ),
    ),
  resetAllExportItems: () =>
    dispatch(individualGroupPerformanceActions.resetAllExportItems()),
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),

  getRecordPaginationResponses: (payload) =>
    dispatch(brandPerformanceActions.getRecordPaginationResponses(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualGroupPerformance);
